import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

    /** @type {boolean} */
  #shouldRestoreDialogOpen = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#markDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#markDialogClosed);

    // Evitar que el drawer se cierre cuando se reemplaza el HTML del section
    this.addEventListener('cart:section-morphed', this.#restoreDialogState);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#markDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#markDialogClosed);
    this.removeEventListener('cart:section-morphed', this.#restoreDialogState);
    this.#historyAbortController?.abort();
  }

  #markDialogOpen = () => {
    this.#shouldRestoreDialogOpen = true;
  };

  #markDialogClosed = () => {
    this.#shouldRestoreDialogOpen = false;
    this.#recoverStaleMobileLock();
  };

  #recoverStaleMobileLock() {
    if (!isMobileBreakpoint()) return;

    const hasAnyOpenDialog = Boolean(document.querySelector('dialog[open]'));
    if (hasAnyOpenDialog) return;

    const previousTop = document.body.style.top;
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (previousTop) {
      const topValue = Number.parseInt(previousTop, 10);
      if (!Number.isNaN(topValue)) {
        window.scrollTo({ top: Math.abs(topValue), behavior: 'instant' });
      }
    }
  }

  /**
   * Restaura el estado del diálogo después de que morphSection reemplaza el HTML.
   * Cuando morphSection reemplaza el section, el <dialog> pierde el atributo open.
   * Aquí forzamos que el drawer permanezca abierto.
   */
  #restoreDialogState = () => {
    // Usamos requestAnimationFrame para esperar a que el DOM se estabilice tras el morph
    requestAnimationFrame(() => {
      const dialog = this.querySelector('dialog');
      if (!dialog) return;

            if (!this.#shouldRestoreDialogOpen) {
        this.#recoverStaleMobileLock();
        return;
      }

      // Si el ref apunta al viejo dialog (desconectado), actualizamos
      if (this.refs.dialog !== dialog) {
        // @ts-ignore - actualizar la referencia interna
        this.refs.dialog = dialog;
      }

      // Si el diálogo no está abierto, lo reabrimos (solo si no hay procesamiento de compra protegida)
      if (!dialog.open && !this.hasAttribute('data-protected-purchase-processing') && !isMobileBreakpoint()) {
        this.showDialog();
      } else {
        this.#recoverStaleMobileLock();
      }
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };
/**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    const cartEvent = /** @type {CustomEvent<{ resource?: { item_count?: number } }>} */ (event);
    const shouldAutoOpen = this.hasAttribute('auto-open') && !isMobileBreakpoint();

    if (shouldAutoOpen) {
      this.showDialog();
    } else {
      this.#recoverStaleMobileLock();
    }

    this.#announceCartCount(cartEvent.detail?.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
